import { randoMessageId } from "../util/author";
import { EventType, EventCallback, Listeners, HeartbeatMessage } from "../types/socket.types";
import {TriggerType} from "../types/taskFlow.types";

/**
 * 用于管理WebSocket连接的服务类
 */
class SocketService {
  private socket: WebSocket | null;
  public isConnected: boolean;
  private reconnectAttempts: number;
  private readonly maxReconnectAttempts: number;
  private reconnectTimeout: number | null;
  private readonly listeners: Listeners;
  private pingInterval: number | null;
  private shouldReconnect: boolean;
  private boardId: string

  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectTimeout = null;
    this.listeners = {
      message: [],
      open: [],
      close: [],
      error: []
    };
    this.pingInterval = null;
    this.shouldReconnect = true;
    this.boardId = ''
  }

   parseImagePushData (arrayBuffer: ArrayBuffer) {
    const dataView = new DataView(arrayBuffer);
    const totalLength = arrayBuffer.byteLength;

    // 1. 先读取最后 src 的长度（最开始的8个字节）
    const srcLength = Number(dataView.getBigUint64(0, false)); // 这里假设是大端序（false）；如果Java写的是小端序要改成 true

    // 2. 然后知道，src 是最后 srcLength 个字节
    const srcStartIndex = totalLength - srcLength;

    const infosBytes = new Uint8Array(arrayBuffer, 8, srcStartIndex - 8); // infos 位于 8 到 srcStartIndex 之间

    const srcBytes = new Uint8Array(arrayBuffer, srcStartIndex, srcLength); // src 位于 srcStartIndex 开始，长度为 srcLength

    // 3. 把 infos 解析成 JSON
    const infosText = new TextDecoder().decode(infosBytes);
    const infos = JSON.parse(infosText);

    // 4. 图片数据，可以用 Blob、URL.createObjectURL 展示
    const blob = new Blob([srcBytes]);
    const imgUrl = URL.createObjectURL(blob);

    return {
      infos,
      imgUrl,
      blob
    };
  }

  /**
   * 将 Blob 转换为 ArrayBuffer
   * @param blob
   */
  revertBlob(blob: Blob) {
    const reader = new FileReader();

    reader.onloadend = () => {
      const arrayBuffer = reader.result;

      if (arrayBuffer instanceof ArrayBuffer) {
        const parsed = this.parseImagePushData(arrayBuffer);
        const data = {
          trigger: TriggerType.SERVER_PUSH_IMAGE,
          data: parsed.infos,
          src: parsed.imgUrl,
          blob: parsed.blob
        }
        this._notifyListeners('message', data);
      } else {
        console.error("转换后的数据不是 ArrayBuffer", arrayBuffer);
      }
    };

    reader.onerror = (error) => {
      console.error("Blob 转换失败", error);
    };

    // 开始读取 Blob 数据
    reader.readAsArrayBuffer(blob);
  }


  getBoardId = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('boardId');
  };


  /**
   * 连接到WebSocket服务器
   * @param {string} url - WebSocket服务器URL
   * @param boardId
   */

  connect(url: string, boardId: string): void {
    this.boardId = boardId;
    try {

      if (this.isConnected || !this.boardId) return;

      if (this.getBoardId() !== this.boardId) return;

      this.socket = new WebSocket(url);
      
      this.socket.onopen = (event: Event) => {
        console.log('WebSocket连接已建立');
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this._setupPing();
        this._notifyListeners('open', event);
      };
      
      this.socket.onmessage = (event: MessageEvent) => {
        try {
          if (typeof event.data === 'string') {
            const data = JSON.parse(event.data);
            if (data.code === 200) {
              this._notifyListeners('message', data);
            }else if(data.code === 4001) {
              this.shouldReconnect = false
              window.location.href = '/abnormalPage?type=2';
            }else {
              this.shouldReconnect = false
              window.location.href = '/abnormalPage?type=1';
            }
          }else if (event.data instanceof Blob) {
            this.revertBlob(event.data)
          }
        } catch (error) {
          console.error('解析WebSocket消息失败:', error);
          this._notifyListeners('message', event.data);
        }
      };
      
      this.socket.onclose = (event: CloseEvent) => {
        console.log('WebSocket连接已关闭', event.code, event.reason);
        this.isConnected = false;
        this._clearPing();
        this._notifyListeners('close', event);
        if (this.shouldReconnect) this._attemptReconnect(url);
      };
      
      this.socket.onerror = (error: Event) => {
        console.error('WebSocket错误:', error);
        this._notifyListeners('error', error);
      };
    } catch (error) {
      console.error('创建WebSocket连接失败:', error);
    }
  }

  /**
   * 向服务器发送消息
   * @param {object|string} data - 要发送的数据
   * @returns {boolean} 是否发送成功
   */
  send(data: object | string): boolean {
    if (!this.isConnected || !this.socket) {
      console.warn('WebSocket未连接，无法发送消息');
      return false;
    }
    
    try {
      const message = typeof data === 'object' ? JSON.stringify(data) : data;
      this.socket.send(message);
      return true;
    } catch (error) {
      console.error('发送WebSocket消息失败:', error);
      return false;
    }
  }

  /**
   * 关闭WebSocket连接
   */
  disconnect(): void {
    if (this.socket) {
      this._clearPing();
      this.socket.close();
      this.socket = null;
      this.isConnected = false;
      this.reconnectAttempts = 0;
      this.shouldReconnect = false
    }
    
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }

  /**
   * 添加事件监听器
   * @param {EventType} event - 事件类型 ('message', 'open', 'close', 'error')
   * @param {EventCallback} callback - 回调函数
   */
  on(event: EventType, callback: EventCallback): void {
    if (this.listeners[event]) {
      this.listeners[event].push(callback);
    }
  }

  /**
   * 移除事件监听器
   * @param {EventType} event - 事件类型
   * @param {EventCallback} callback - 要移除的回调函数
   */
  off(event: EventType, callback: EventCallback): void {
    if (this.listeners[event]) {
      this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
    }
  }

  /**
   * 设置心跳包
   */
  private _setupPing(): void {
    this._clearPing();
    this.pingInterval = window.setInterval(() => {
      if (this.isConnected) {
        const heartbeat: HeartbeatMessage = {
          command: "heartMessage", 
          messageId: randoMessageId()
        };
        this.send(heartbeat);
      }
    }, 30000); // 每30秒发送一次心跳
  }

  /**
   * 清除心跳包
   */
  private _clearPing(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  /**
   * 尝试重新连接
   * @param {string} url - WebSocket服务器URL
   */
  private _attemptReconnect(url: string): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
      
      console.log(`尝试在 ${delay}ms 后重新连接... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
      
      this.reconnectTimeout = window.setTimeout(() => {
        this.connect(url, this.boardId);
      }, delay);
    }
  }

  /**
   * 通知所有监听器
   * @param {EventType} event - 事件类型
   * @param {any} data - 事件数据
   */
  private _notifyListeners(event: EventType, data: any): void {
    if (this.listeners[event]) {
      this.listeners[event].forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`执行 ${event} 监听器时出错:`, error);
        }
      });
    }
  }
}

// 导出单例实例
export const socketService = new SocketService(); 